import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, DollarSign, Award, Star, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const LiveSuccessMetrics = () => {
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    activeClients: 0,
    totalRevenue: 0,
    successRate: 0,
    clientsToday: 0,
    avgRating: 4.8
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/success-metrics`);
      setMetrics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  if (loading) {
    return null; // Don't show skeleton, just hide until loaded
  }

  return (
    <section className="py-12 md:py-16 px-4 bg-gradient-to-br from-blue-600 to-blue-700 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-white text-sm font-medium">Live Metrics</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
            Real-Time Success <span className="text-blue-200">Metrics</span>
          </h2>
          <p className="text-blue-100 max-w-2xl mx-auto">
            See our impact in real-time. Numbers that speak louder than words.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {/* Total Clients */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {formatNumber(metrics.totalClients)}+
              </div>
              <div className="text-xs md:text-sm text-blue-100">Total Clients</div>
            </div>
          </div>

          {/* Active Clients */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <CheckCircle className="w-6 h-6 text-green-300" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {formatNumber(metrics.activeClients)}+
              </div>
              <div className="text-xs md:text-sm text-blue-100">Active Now</div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <DollarSign className="w-6 h-6 text-yellow-300" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                ₹{formatNumber(metrics.totalRevenue)}
              </div>
              <div className="text-xs md:text-sm text-blue-100">Revenue Generated</div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6 text-green-300" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {metrics.successRate}%
              </div>
              <div className="text-xs md:text-sm text-blue-100">Success Rate</div>
            </div>
          </div>

          {/* Clients Today */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Award className="w-6 h-6 text-orange-300" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {metrics.clientsToday}
              </div>
              <div className="text-xs md:text-sm text-blue-100">Helped Today</div>
            </div>
          </div>

          {/* Rating */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Star className="w-6 h-6 text-yellow-300 fill-yellow-300" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {metrics.avgRating}
              </div>
              <div className="text-xs md:text-sm text-blue-100">Average Rating</div>
            </div>
          </div>
        </div>

        {/* Auto-update indicator */}
        <div className="text-center mt-8">
          <div className="inline-flex items-center space-x-2 text-blue-100 text-xs">
            <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
            <span>Updates every 30 seconds</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveSuccessMetrics;
